// ==================== 场景管理器 ====================

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { PanoramaLoader } from './PanoramaLoader.js';
import { NPCController } from './NPCController.js';
import type { SceneConfig, CameraMode } from '../types/index.js';

export class SceneManager {
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private controls!: OrbitControls;
  private clock: THREE.Clock = new THREE.Clock();

  private panoramaLoader: PanoramaLoader;
  private npcController: NPCController;
  private currentPanorama: THREE.Mesh | null = null;

  private cameraMode: CameraMode = 'free';
  private followTargetNpcId: string | null = null;

  private gridHelper: THREE.GridHelper | null = null;
  private gridVisible = false;

  private animationFrameId = 0;

  constructor(private container: HTMLElement) {
    this.panoramaLoader = new PanoramaLoader();
    this.init();  // 必须先初始化 this.scene
    this.npcController = new NPCController(this.scene);
  }

  private init(): void {
    // 渲染器
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.container.appendChild(this.renderer.domElement);

    // 场景
    this.scene = new THREE.Scene();

    // 相机
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.container.clientWidth / this.container.clientHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 8, 12);

    // 控制器
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 2;
    this.controls.maxDistance = 50;
    this.controls.maxPolarAngle = Math.PI * 0.85;

    // 默认光照
    this.setupDefaultLighting();

    // 事件
    window.addEventListener('resize', this.onResize.bind(this));

    // 启动渲染循环
    this.animate();
  }

  private setupDefaultLighting(): void {
    const ambient = new THREE.AmbientLight(0x404060, 0.6);
    this.scene.add(ambient);

    const dir = new THREE.DirectionalLight(0xffffff, 1.0);
    dir.position.set(10, 20, 5);
    this.scene.add(dir);
  }

  // ==================== 全景图 ====================

  async loadScene(sceneConfig: SceneConfig): Promise<void> {
    // 移除旧全景图
    if (this.currentPanorama) {
      this.scene.remove(this.currentPanorama);
      this.currentPanorama = null;
    }

    // 加载新全景图
    try {
      this.currentPanorama = await this.panoramaLoader.loadPanorama(sceneConfig.panoramaPath);
    } catch {
      console.warn('全景图加载失败，使用占位背景');
      this.currentPanorama = this.panoramaLoader.createPlaceholderPanorama();
    }
    this.scene.add(this.currentPanorama);

    // 设置相机
    if (sceneConfig.cameraInitial) {
      this.camera.position.set(...sceneConfig.cameraInitial);
    }
    if (sceneConfig.cameraLookAt) {
      this.controls.target.set(...sceneConfig.cameraLookAt);
    }

    // 设置光照
    if (sceneConfig.lighting) {
      this.applyLightingConfig(sceneConfig.lighting);
    }

    // 生成 NPC
    sceneConfig.npcs.forEach((npcConfig) => {
      this.npcController.spawnNPC(npcConfig);
    });
  }

  private applyLightingConfig(lighting: SceneConfig['lighting']): void {
    if (!lighting) return;
    // 清除默认光照 (简化处理：不清除，叠加)
    if (lighting.ambient) {
      const a = new THREE.AmbientLight(
        new THREE.Color(lighting.ambient.color),
        lighting.ambient.intensity
      );
      this.scene.add(a);
    }
    if (lighting.directional) {
      const d = new THREE.DirectionalLight(
        new THREE.Color(lighting.directional.color),
        lighting.directional.intensity
      );
      d.position.set(...lighting.directional.position);
      this.scene.add(d);
    }
  }

  // ==================== 相机模式 ====================

  setCameraMode(mode: CameraMode, npcId?: string): void {
    this.cameraMode = mode;
    this.followTargetNpcId = npcId || null;

    switch (mode) {
      case 'free':
        this.controls.enabled = true;
        break;
      case 'top-down':
        this.controls.enabled = false;
        this.camera.position.set(0, 30, 0);
        this.camera.lookAt(0, 0, 0);
        break;
      case 'follow-npc':
        this.controls.enabled = true;
        if (npcId) {
          const npc = this.npcController.getNPC(npcId);
          if (npc?.mesh) {
            this.controls.target.copy(npc.mesh.position);
          }
        }
        break;
    }
  }

  // ==================== 网格 ====================

  toggleGrid(): void {
    if (!this.gridHelper) {
      this.gridHelper = new THREE.GridHelper(30, 30, 0x444444, 0x222222);
      this.gridHelper.position.y = -0.01; // 略低于地面
    }
    this.gridVisible = !this.gridVisible;
    if (this.gridVisible) {
      this.scene.add(this.gridHelper);
    } else {
      this.scene.remove(this.gridHelper);
    }
  }

  // ==================== NPC 操作代理 ====================

  getNPCController(): NPCController {
    return this.npcController;
  }

  // ==================== 渲染循环 ====================

  private animate(): void {
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));

    const delta = this.clock.getDelta();

    // 跟随模式
    if (this.cameraMode === 'follow-npc' && this.followTargetNpcId) {
      const npc = this.npcController.getNPC(this.followTargetNpcId);
      if (npc?.mesh) {
        this.controls.target.lerp(npc.mesh.position, 0.05);
      }
    }

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  private onResize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  dispose(): void {
    cancelAnimationFrame(this.animationFrameId);
    window.removeEventListener('resize', this.onResize.bind(this));
    this.panoramaLoader.dispose();
    this.npcController.dispose();
    this.renderer.dispose();
    this.controls.dispose();
  }
}
